import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PaymentIcon } from 'react-svg-credit-card-payment-icons';
import styles from './HomeOwnerPlanCheckout.module.css';
import { homeownerBillingService } from '../../services/api';
import PaymentMethodModal from './billing/PaymentMethodModal';
import BillingAddressModal from './billing/BillingAddressModal';

const HomeOwnerPlanCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedPlan] = useState(location.state?.selectedPlan || null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [billingAddresses, setBillingAddresses] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState(null);
  const [isNewBillingAddress, setIsNewBillingAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [nextBillingDate, setNextBillingDate] = useState(null);
  const [billingInfo, setBillingInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    saveAddress: true
  });
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  useEffect(() => {
    if (!selectedPlan) {
      navigate('/homeowner/pricing');
      return;
    }
    fetchPaymentMethods();
    fetchBillingAddresses();
    calculateNextBillingDate();
  }, [selectedPlan, navigate]);

  const fetchBillingAddresses = async () => {
    try {
      const response = await homeownerBillingService.getBillingAddresses();
      setBillingAddresses(response.data.data);
      // Auto-select default billing address if exists
      const defaultAddress = response.data.data.find(address => address.is_default);
      if (defaultAddress) {
        setSelectedBillingAddress(defaultAddress.id);
        setBillingInfo(prevInfo => ({
          ...prevInfo,
          ...mapBillingAddressToForm(defaultAddress)
        }));
      }
    } catch (error) {
      toast.error('Failed to load billing addresses');
    }
  };

  const mapBillingAddressToForm = (address) => ({
    firstName: address.first_name,
    lastName: address.last_name,
    email: address.email,
    addressLine1: address.address_line1,
    addressLine2: address.address_line2 || '',
    city: address.city,
    state: address.state,
    postalCode: address.postal_code,
    country: address.country
  });

  const handleBillingAddressSelect = (addressId) => {
    setSelectedBillingAddress(addressId);
    setIsNewBillingAddress(false);
    const selectedAddress = billingAddresses.find(addr => addr.id === addressId);
    if (selectedAddress) {
      setBillingInfo(prevInfo => ({
        ...prevInfo,
        ...mapBillingAddressToForm(selectedAddress)
      }));
    }
  };

  const calculateNextBillingDate = () => {
    const today = new Date();
    const nextDate = new Date(today);
    
    // Set next billing date based on billing cycle
    if (selectedPlan?.billingCycle === 'monthly') {
      nextDate.setMonth(today.getMonth() + 1);
    } else if (selectedPlan?.billingCycle === 'yearly') {
      nextDate.setFullYear(today.getFullYear() + 1);
    }
    
    setNextBillingDate(nextDate);
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await homeownerBillingService.getPaymentMethods();
      setPaymentMethods(response.data.data);
      // Auto-select default payment method if exists
      const defaultMethod = response.data.data.find(method => method.is_default);
      if (defaultMethod) {
        setSelectedPaymentMethod(defaultMethod.id);
      }
    } catch (error) {
      toast.error('Failed to load payment methods');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBillingInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBillingInfo(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateBillingInfo = () => {
    const required = [
      'firstName', 'lastName', 'email', 'addressLine1',
      'city', 'state', 'postalCode', 'country'
    ];
    const missing = required.filter(field => !billingInfo[field]);
    
    if (missing.length > 0) {
      toast.error('Please fill in all required billing information fields');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(billingInfo.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleSaveAddress = async (addressData) => {
    try {
      if (isEditingAddress) {
        // Update existing address
        await homeownerBillingService.updateBillingAddress(selectedBillingAddress, addressData);
        toast.success('Billing address updated successfully');
      } else {
        // Create new address
        const response = await homeownerBillingService.createBillingAddress({
          ...addressData,
          is_default: addressData.is_default || billingAddresses.length === 0
        });
        setSelectedBillingAddress(response.data.data.id);
        toast.success('Billing address added successfully');
      }
      
      await fetchBillingAddresses();
      setIsAddressModalOpen(false);
      setIsNewBillingAddress(false);
      setIsEditingAddress(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save billing address');
      throw error;
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPaymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    if (!selectedBillingAddress && !validateBillingInfo()) {
      return;
    }

    setIsProcessing(true);
    try {
      let billingAddressId = selectedBillingAddress;

      // If it's a new billing address and user wants to save it
      if (!selectedBillingAddress && billingInfo.saveAddress) {
        const addressResponse = await homeownerBillingService.createBillingAddress({
          first_name: billingInfo.firstName,
          last_name: billingInfo.lastName,
          email: billingInfo.email,
          address_line1: billingInfo.addressLine1,
          address_line2: billingInfo.addressLine2,
          city: billingInfo.city,
          state: billingInfo.state,
          postal_code: billingInfo.postalCode,
          country: billingInfo.country,
          is_default: billingAddresses.length === 0 // Make default if first address
        });
        billingAddressId = addressResponse.data.data.id;
      }

      const subscriptionData = {
        planId: selectedPlan.id,
        planName: selectedPlan.name,
        price: parseFloat(selectedPlan.finalPrice || selectedPlan.price),
        billingCycle: selectedPlan.billingCycle,
        paymentMethodId: selectedPaymentMethod,
        billingAddressId,
        propertyType: selectedPlan.propertyType,
        propertySize: selectedPlan.propertySize,
        serviceFrequency: selectedPlan.serviceFrequency,
        billingInfo: !billingAddressId ? {
          firstName: billingInfo.firstName,
          lastName: billingInfo.lastName,
          email: billingInfo.email,
          addressLine1: billingInfo.addressLine1,
          addressLine2: billingInfo.addressLine2,
          city: billingInfo.city,
          state: billingInfo.state,
          postalCode: billingInfo.postalCode,
          country: billingInfo.country
        } : undefined
      };

      const response = await homeownerBillingService.updateSubscription(subscriptionData);

      if (response.data.status === 'success') {
        toast.success('Successfully subscribed to plan!');
        navigate('/homeowner/billing', { 
          replace: true 
        });
      } else {
        throw new Error(response.data.message || 'Failed to process subscription');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.message || 'Failed to process subscription');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSetDefaultAddress = async (addressId) => {
    try {
      const address = billingAddresses.find(a => a.id === addressId);
      
      await homeownerBillingService.updateBillingAddress(addressId, {
        first_name: address.first_name,
        last_name: address.last_name,
        email: address.email,
        address_line1: address.address_line1,
        address_line2: address.address_line2,
        city: address.city,
        state: address.state,
        postal_code: address.postal_code,
        country: address.country,
        is_default: true
      });
      
      await fetchBillingAddresses();
      toast.success('Default billing address updated');
    } catch (error) {
      toast.error('Failed to set default address');
    }
  };

  const handleSavePaymentMethod = async (paymentData) => {
    try {
      const response = await homeownerBillingService.addPaymentMethod({
        cardNumber: paymentData.cardNumber.replace(/\s/g, ''),
        cardHolder: paymentData.cardHolder,
        expiryMonth: paymentData.expiryMonth,
        expiryYear: paymentData.expiryYear,
        isDefault: paymentData.isDefault
      });

      if (response.data.status === 'success') {
        await fetchPaymentMethods();
        setIsPaymentModalOpen(false);
        toast.success('Payment method added successfully');
      } else {
        throw new Error(response.data.message || 'Failed to save payment method');
      }
    } catch (error) {
      console.error('Payment method error:', error);
      toast.error(error.response?.data?.message || 'Failed to save payment method');
      return error;
    }
  };

  const handleSetDefaultPaymentMethod = async (methodId) => {
    try {
      const method = paymentMethods.find(m => m.id === methodId);
      
      await homeownerBillingService.updatePaymentMethod(methodId, {
        cardHolder: method.card_holder_name,
        expiryMonth: method.expiry_month,
        expiryYear: method.expiry_year,
        isDefault: true
      });
      
      await fetchPaymentMethods();
      toast.success('Default payment method updated');
    } catch (error) {
      toast.error('Failed to set default payment method');
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}>
          <i className="fas fa-spinner fa-spin"></i>
        </div>
        <p>Loading payment information...</p>
      </div>
    );
  }

  if (!selectedPlan) {
    return null;
  }

  return (
    <div className={styles.checkoutContainer}>
      <div className={styles.checkoutCard}>
        <div className={styles.planSummary}>
          <h2>Complete Your Subscription</h2>
          <div className={styles.planDetails}>
            <h3>{selectedPlan.name}</h3>
            <p className={styles.price}>
              ${(selectedPlan.finalPrice || selectedPlan.price).toFixed(2)}
              <span className={styles.billingCycle}>
                /{selectedPlan.billingCycle}
              </span>
            </p>
            <ul className={styles.features}>
              {selectedPlan.features.map((feature, index) => (
                <li key={index}>
                  <i className="fas fa-check-circle"></i>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className={styles.billingInfoSection}>
          <div className={styles.billingHeader}>
            <h3>Billing Information</h3>
            <button
              className={styles.addAddressButton}
              onClick={() => {
                setIsNewBillingAddress(true);
                setIsEditingAddress(false);
                setSelectedBillingAddress(null);
                setIsAddressModalOpen(true);
                setBillingInfo({
                  firstName: '',
                  lastName: '',
                  email: '',
                  addressLine1: '',
                  addressLine2: '',
                  city: '',
                  state: '',
                  postalCode: '',
                  country: '',
                  saveAddress: true
                });
              }}
            >
              <i className="fas fa-plus"></i> Add New Address
            </button>
          </div>
          
          {billingAddresses.length > 0 && (
            <div className={styles.savedAddresses}>
              <div className={styles.addressList}>
                {billingAddresses.map(address => (
                  <div
                    key={address.id}
                    className={`${styles.savedAddress} ${
                      selectedBillingAddress === address.id ? styles.selected : ''
                    }`}
                  >
                    <div 
                      className={styles.addressInfo}
                      onClick={() => handleBillingAddressSelect(address.id)}
                    >
                      <span className={styles.addressName}>
                        {address.first_name} {address.last_name}
                        {Boolean(address.is_default) && (
                          <span className={styles.defaultBadge}>
                            <i className="fas fa-star"></i> Default
                          </span>
                        )}
                      </span>
                      <span>{address.address_line1}</span>
                      {address.address_line2 && <span>{address.address_line2}</span>}
                      <span>{address.city}, {address.state} {address.postal_code}</span>
                      <span>{address.country}</span>
                    </div>
                    <div className={styles.addressActions}>
                      <button
                        className={styles.editButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedBillingAddress(address.id);
                          setIsEditingAddress(true);
                          setIsAddressModalOpen(true);
                          setBillingInfo({
                            firstName: address.first_name,
                            lastName: address.last_name,
                            email: address.email,
                            addressLine1: address.address_line1,
                            addressLine2: address.address_line2 || '',
                            city: address.city,
                            state: address.state,
                            postalCode: address.postal_code,
                            country: address.country,
                            saveAddress: true
                          });
                        }}
                      >
                        <i className="fas fa-pencil-alt"></i> Edit
                      </button>
                      {!Boolean(address.is_default) && (
                        <button
                          className={styles.setDefaultButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetDefaultAddress(address.id);
                          }}
                        >
                          <i className="fas fa-star"></i> Set Default
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {billingAddresses.length === 0 && (
            <div className={styles.noAddresses}>
              <i className="fas fa-home" style={{ fontSize: '32px', color: '#94a3b8', marginBottom: '16px' }}></i>
              <p>No billing addresses available</p>
            </div>
          )}
        </div>

        <div className={styles.paymentSection}>
          <div className={styles.paymentHeader}>
            <h3>Select Payment Method</h3>
            <button
              className={styles.addPaymentButton}
              onClick={() => setIsPaymentModalOpen(true)}
            >
              <i className="fas fa-plus"></i> Add Payment Method
            </button>
          </div>
          {paymentMethods.length === 0 ? (
            <div className={styles.noPaymentMethods}>
              <i className="fas fa-credit-card" style={{ fontSize: '32px', color: '#94a3b8', marginBottom: '16px' }}></i>
              <p>No payment methods available</p>
            </div>
          ) : (
            <div className={styles.paymentMethods}>
              {paymentMethods.map(method => (
                <div
                  key={method.id}
                  className={`${styles.paymentMethod} ${
                    selectedPaymentMethod === method.id ? styles.selected : ''
                  }`}
                  onClick={() => setSelectedPaymentMethod(method.id)}
                  data-brand={method.card_brand.toLowerCase()}
                >
                  <div className={styles.cardInfo}>
                    <div className={styles.cardIconWrapper}>
                      <PaymentIcon 
                        type={method.card_brand.toLowerCase()} 
                        format="flat" 
                        width={48} 
                      />
                    </div>
                    <div>
                      <span className={styles.cardNumber}>•••• {method.last_four}</span>
                      <span className={styles.cardHolder}>{method.card_holder_name}</span>
                      <span className={styles.expiryDate}>
                        Expires {method.expiry_month}/{method.expiry_year}
                      </span>
                    </div>
                  </div>
                  {Boolean(method.is_default) ? (
                    <span className={styles.defaultBadge}>
                      <i className="fas fa-star"></i> Default
                    </span>
                  ) : (
                    <button
                      className={styles.defaultButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetDefaultPaymentMethod(method.id);
                      }}
                    >
                      <i className="fas fa-star"></i> Set Default
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.summary}>
          <div className={styles.summaryRow}>
            <span>Plan Cost</span>
            <span>${(selectedPlan.finalPrice || selectedPlan.price).toFixed(2)}</span>
          </div>
          <div className={styles.summaryRow}>
            <span>Billing Cycle</span>
            <span>{selectedPlan.billingCycle}</span>
          </div>
          {selectedPaymentMethod && nextBillingDate && (
            <div className={styles.nextBillingInfo}>
              <div className={styles.summaryRow}>
                <span>Next Billing Date</span>
                <span>{nextBillingDate.toLocaleDateString()}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Payment Method</span>
                <span>
                  {paymentMethods.find(m => m.id === selectedPaymentMethod)?.card_brand} 
                   &nbsp;ending in {paymentMethods.find(m => m.id === selectedPaymentMethod)?.last_four}
                </span>
              </div>
              {selectedBillingAddress && (
                <div className={styles.summaryRow}>
                  <span>Billing Address</span>
                  <span>
                    {billingAddresses.find(a => a.id === selectedBillingAddress)?.address_line1}
                  </span>
                </div>
              )}
            </div>
          )}
          <div className={styles.summaryTotal}>
            <span>Total Due Today</span>
            <span>${(selectedPlan.finalPrice || selectedPlan.price).toFixed(2)}</span>
          </div>
        </div>

        <div className={styles.actions}>
          <button
            className={styles.cancelButton}
            onClick={() => navigate('/homeowner/pricing')}
            disabled={isProcessing}
          >
            <i className="fas fa-arrow-left"></i> Back to Plans
          </button>
          <button
            className={styles.subscribeButton}
            onClick={handleSubscribe}
            disabled={isProcessing || !selectedPaymentMethod}
          >
            {isProcessing ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Processing...
              </>
            ) : (
              <>
                <i className="fas fa-lock"></i>
                Confirm Subscription
              </>
            )}
          </button>
        </div>
      </div>

      {/* Payment Method Modal */}
      <PaymentMethodModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSave={handleSavePaymentMethod}
      />

      {/* Billing Address Modal */}
      <BillingAddressModal
        isOpen={isAddressModalOpen}
        onClose={() => {
          setIsAddressModalOpen(false);
          setIsEditingAddress(false);
          setIsNewBillingAddress(false);
        }}
        onSave={handleSaveAddress}
        editingAddress={isEditingAddress ? billingAddresses.find(a => a.id === selectedBillingAddress) : null}
      />
    </div>
  );
};

export default HomeOwnerPlanCheckout; 